import { useEffect, useRef, useState } from "react";
import { err } from ".";

/**
 * Shape of the `externalWidget` block on a booking-handoff form step.
 *
 * This mirrors `FormStepExternalWidget["externalWidget"]` from
 * `stentor-models@1.75.0`. See the local type-extension note in
 * `FormStepDisplay.tsx` for why it is redeclared here instead of imported.
 */
export interface ExternalWidgetConfig {
    anchorId: string;
    scriptSrc: string;
    configGlobal: string;
    config: Record<string, string | number | boolean>;
    successCallbackKey?: string;
    cacheBust?: boolean;
    renderTimeoutMs?: number;
    fallbackStep?: string;
}

export type ExternalWidgetOutcome = "pending" | "rendered" | "no_render" | "error";

export interface UseExternalWidgetOptions {
    /**
     * Fired once the anchor renders a bookable `#step-*` (any step other than a
     * no-match or loading step, e.g. `step-schedule` or the returning-visitor
     * `step-review`).
     */
    onRendered?: (provider: string) => void;
    /**
     * Fired when the anchor renders a no-match step (no coverage), or stays empty
     * with no `#step-loading` past the backstop `renderTimeoutMs`.
     */
    onNoRender?: (provider: string) => void;
    /**
     * Fired at most once per mounted handoff, the first time the partner script invokes
     * the configured success callback. Later invocations within the same handoff are
     * suppressed, reported to the console and counted (see `suppressedScheduledCount`)
     * -- CostGuide confirmed (2026-08-13) that it fires "whenever an appointment gets
     * scheduled", so a single handoff can produce more than one fire and a duplicate
     * dispatch double-counts the booking downstream.
     *
     * Suppression is deliberately NOT its own analytics event: a new eventType needs a
     * matching server-side eventName mapping in stentor-api, which is out of scope here.
     * The console error is the signal that works today in a visitor's browser, and
     * `suppressedScheduledCount` is there for a caller that wants to act on it.
     *
     * `payload` is whatever (if anything) the partner passes to it. CostGuide passes
     * nothing today -- it is a bare trigger with no appointment id, time or contractor --
     * but the parameter is kept tolerant in case that changes.
     */
    onScheduled?: (provider: string, payload?: unknown) => void;
    /**
     * Fired when the script fails to load, or the scriptSrc is rejected.
     */
    onError?: (provider: string) => void;
    /**
     * Fired alongside onNoRender/onError when `externalWidget.fallbackStep` is set,
     * so the caller can navigate there. Not fired when fallbackStep is unset --
     * the caller is expected to render an inline fallback message instead.
     */
    onFallback?: (fallbackStep: string) => void;
}

const DEFAULT_RENDER_TIMEOUT_MS = 8000;

// The partner form renders exactly one `#step-*` container at a time with stable
// ids, so the step id -- not a blind "anchor has children" check -- is the render
// signal. Render timing was measured at 111ms-18.7s against the same endpoint, so
// a fixed timer can only ever be a backstop for "the script never arrived".
const LOADING_STEP_ID = "step-loading";
const NO_MATCH_STEP_IDS = [
    "step-nomatch",
    "step-nomatchaggregator",
    "step-no-match-financing",
    "step-payPerCallNoneAvailable",
];
// While the anchor still shows `#step-loading` at timeout we extend rather than
// fail (the 18.7s worst case would otherwise dead-end a homeowner after we already
// took their details). Bounded so a stuck loader eventually settles as no_render.
const MAX_RENDER_TIMEOUT_EXTENSIONS = 3;

type AnchorState = "rendered" | "no_coverage" | "loading" | "empty";

/**
 * Classifies the current state of the anchor from the partner step id it shows.
 *
 * - a no-match step id  -> "no_coverage" (a real screen, but no bookable coverage)
 * - `step-loading`      -> "loading" (still working; do not fail yet)
 * - any other `step-*`  -> "rendered" (e.g. `step-schedule`, or the
 *                          returning-visitor `step-review` -- do NOT assume one)
 * - no `step-*` present -> "empty" (not rendered yet)
 */
export function classifyAnchor(anchor: HTMLElement): AnchorState {
    const stepEls = anchor.querySelectorAll('[id^="step-"]');
    if (stepEls.length === 0) {
        return "empty";
    }
    let hasLoading = false;
    for (let i = 0; i < stepEls.length; i++) {
        const id = stepEls[i].id;
        if (NO_MATCH_STEP_IDS.indexOf(id) !== -1) {
            return "no_coverage";
        }
        if (id === LOADING_STEP_ID) {
            hasLoading = true;
            continue;
        }
        return "rendered";
    }
    return hasLoading ? "loading" : "empty";
}

/**
 * Derives the analytics `provider` string from the partner script's hostname.
 */
export function getExternalWidgetProvider(scriptSrc: string | undefined): string {
    if (!scriptSrc) {
        return "unknown";
    }
    try {
        return new URL(scriptSrc).hostname;
    } catch {
        return "unknown";
    }
}

function isValidScriptSrc(scriptSrc: string): boolean {
    try {
        const parsed = new URL(scriptSrc);
        return parsed.protocol === "https:";
    } catch {
        return false;
    }
}

// Scripts are appended to document.body and never removed. A module-level guard
// (mirroring useTrustedForm's existingScript check) ensures the same partner
// origin is only loaded once per page, even across separate mounted instances.
const SCRIPT_ORIGIN_ATTRIBUTE = "data-xapp-external-widget-origin";

/**
 * Drives the mount sequence for a third-party booking-widget handoff step:
 * assigns the merged config to `window[configGlobal]` (including the wired-up
 * success callback), appends the partner's embed script, and watches the
 * anchor for a render-timeout outcome.
 *
 * The mount sequence runs at most once per component lifetime (guarded by a
 * ref) the first time `externalWidget` is defined. If the config object is
 * later replaced with a new reference (e.g. a slower FORM_SUBMIT response
 * merging server values in after the step already rendered), the global is
 * re-assigned with the latest values but the script is not re-appended and
 * the timer is not restarted.
 */
export function useExternalWidget(
    externalWidget: ExternalWidgetConfig | undefined,
    options: UseExternalWidgetOptions = {}
): { outcome: ExternalWidgetOutcome; suppressedScheduledCount: number } {
    // onScheduled is read from optionsRef inside the mount effect (so a late
    // callback change is picked up without re-running the effect), not destructured here.
    const { onRendered, onNoRender, onError, onFallback } = options;

    const [outcome, setOutcome] = useState<ExternalWidgetOutcome>("pending");
    // Repeats are suppressed, not discarded: if the partner starts firing twice for every
    // booking we want that visible rather than merely harmless.
    const [suppressedScheduledCount, setSuppressedScheduledCount] = useState(0);

    const hasMountedRef = useRef(false);
    // Per mounted handoff, so a genuine re-mount (the visitor returns and books again) is a
    // new handoff and may fire again. Survives a refreshed config replacing the global.
    const scheduledFiredRef = useRef(false);
    const suppressedScheduledRef = useRef(0);
    const outcomeSettledRef = useRef(false);
    const optionsRef = useRef(options);
    optionsRef.current = options;

    useEffect(() => {
        if (!externalWidget) {
            return undefined;
        }

        const provider = getExternalWidgetProvider(externalWidget.scriptSrc);

        // Config may be refreshed (e.g. by a merged FORM_SUBMIT response) on a later
        // render of the same mounted step -- keep the global current either way.
        const globalConfig: Record<string, unknown> = { ...externalWidget.config };
        if (externalWidget.successCallbackKey) {
            const successCallbackKey = externalWidget.successCallbackKey;
            globalConfig[successCallbackKey] = (payload?: unknown) => {
                if (scheduledFiredRef.current) {
                    suppressedScheduledRef.current += 1;
                    // err, NOT log: `log` is gated on window.xaLogLevel === "debug", which no
                    // visitor's browser sets, so a repeat in the wild would be invisible -- the
                    // opposite of the point. A partner firing its success hook twice for one
                    // booking is an anomaly worth a console error, the same treatment this hook
                    // already gives a rejected script origin and a failed script load.
                    err(
                        `[ExternalWidget] Suppressed repeat ${successCallbackKey} fire ` +
                            `#${suppressedScheduledRef.current} from ${provider}`,
                        payload
                    );
                    setSuppressedScheduledCount(suppressedScheduledRef.current);
                    return;
                }
                scheduledFiredRef.current = true;
                optionsRef.current.onScheduled?.(provider, payload);
            };
        }
        (window as unknown as Record<string, unknown>)[externalWidget.configGlobal] = globalConfig;

        if (hasMountedRef.current) {
            return undefined;
        }
        hasMountedRef.current = true;

        const settle = (nextOutcome: ExternalWidgetOutcome, notify: (() => void) | undefined) => {
            if (outcomeSettledRef.current) {
                return;
            }
            outcomeSettledRef.current = true;
            setOutcome(nextOutcome);
            notify?.();
            if (nextOutcome !== "rendered" && externalWidget.fallbackStep) {
                onFallback?.(externalWidget.fallbackStep);
            }
        };

        if (!isValidScriptSrc(externalWidget.scriptSrc)) {
            err(
                `[ExternalWidget] Rejected script URL from unexpected origin: ${externalWidget.scriptSrc}`
            );
            settle("error", () => onError?.(provider));
            return undefined;
        }

        const anchor = document.getElementById(externalWidget.anchorId);
        const timeoutMs = externalWidget.renderTimeoutMs ?? DEFAULT_RENDER_TIMEOUT_MS;

        let timeoutId: ReturnType<typeof setTimeout> | undefined;
        let observer: MutationObserver | undefined;
        let extensions = 0;

        const handleRendered = () => settle("rendered", () => onRendered?.(provider));
        const handleNoRender = () => settle("no_render", () => onNoRender?.(provider));

        // Settle on a decisive step id. Returns true once the outcome is settled.
        const evaluate = (el: HTMLElement | null): boolean => {
            if (!el) {
                return false;
            }
            const state = classifyAnchor(el);
            if (state === "rendered") {
                handleRendered();
                return true;
            }
            if (state === "no_coverage") {
                handleNoRender();
                return true;
            }
            return false; // "loading" | "empty" -> keep waiting
        };

        const onTimeout = () => {
            if (outcomeSettledRef.current) {
                return;
            }
            const currentAnchor = document.getElementById(externalWidget.anchorId);
            if (evaluate(currentAnchor)) {
                return;
            }
            // Still loading? Extend rather than declare a false no-render.
            if (
                currentAnchor &&
                classifyAnchor(currentAnchor) === "loading" &&
                extensions < MAX_RENDER_TIMEOUT_EXTENSIONS
            ) {
                extensions += 1;
                timeoutId = setTimeout(onTimeout, timeoutMs);
                return;
            }
            // Empty past the backstop (or stuck loading) -> hard failure.
            handleNoRender();
        };

        const settledOnMount = evaluate(anchor);
        if (!settledOnMount) {
            if (anchor) {
                observer = new MutationObserver(() => {
                    evaluate(document.getElementById(externalWidget.anchorId));
                });
                observer.observe(anchor, { childList: true, subtree: true });
            }
            timeoutId = setTimeout(onTimeout, timeoutMs);
        }

        let origin: string;
        try {
            origin = new URL(externalWidget.scriptSrc).origin;
        } catch {
            origin = externalWidget.scriptSrc;
        }

        const existingScript = document.querySelector(
            `script[${SCRIPT_ORIGIN_ATTRIBUTE}="${origin}"]`
        );

        if (!existingScript) {
            const script = document.createElement("script");
            script.src = externalWidget.cacheBust
                ? `${externalWidget.scriptSrc}${
                      externalWidget.scriptSrc.includes("?") ? "&" : "?"
                  }v=${Date.now()}`
                : externalWidget.scriptSrc;
            script.setAttribute(SCRIPT_ORIGIN_ATTRIBUTE, origin);
            script.onerror = () => {
                err(`[ExternalWidget] Failed to load script: ${externalWidget.scriptSrc}`);
                settle("error", () => onError?.(provider));
            };
            // Intentionally no cleanup on unmount -- mirrors useTrustedForm.
            document.body.appendChild(script);
        }

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            observer?.disconnect();
        };
        // Only the identity of externalWidget should re-trigger the mount sequence;
        // the notify callbacks are read from optionsRef to avoid re-running on every
        // parent re-render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [externalWidget]);

    return { outcome, suppressedScheduledCount };
}
